/**
 * Gössl Wishlist + Outfit Groups
 * localStorage for all visitors, syncs to Xano for logged-in customers.
 *
 * APIs:
 *   window.Goessl.wishlist  — flat wishlist (array of handles)
 *   window.Goessl.outfits   — outfit groups (array of outfit objects)
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'goessl_wishlist';
  var OUTFITS_KEY = 'goessl_outfits';
  // r39d: provenance for pieces added while browsing in a Geschäft (store
  // mode via QR card) — {handle: {page, name, ts}}, parallel to the flat list
  // so the handle-array contract stays untouched.
  var SEEN_KEY = 'goessl_wishlist_seen';
  // 2026-06-10: repointed from Xano to the CF Worker (Xano retires with the
  // Intex sunset). Identical request/response contract, KV-backed server-side.
  var XANO_BASE = 'https://goessl-cron-jobs.asksj.workers.dev';
  var syncDebounceTimer = null;
  var serverSynced = false;

  // ═══════════════════════════════════════════
  //  WISHLIST (flat list of product handles)
  // ═══════════════════════════════════════════

  function getItems() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: items } }));
  }

  function hasItem(handle) {
    return getItems().indexOf(handle) !== -1;
  }

  function getSeen() {
    try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || {}; }
    catch (e) { return {}; }
  }
  function saveSeen(m) {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(m)); } catch (e) {}
  }

  // r39g (SJ): saving to the Merkliste needs an account — a guest tap opens
  // the sign-in prompt instead, and the intended piece is stashed so it lands
  // on the list right after login/registration.
  var PENDING_KEY = 'g26_pending_wish';

  function promptSignIn() {
    if (document.querySelector('.g26-auth')) return;
    var opener = document.activeElement; // r71: restore focus on dismiss
    var wrap = document.createElement('div');
    wrap.className = 'g26-auth';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-label', window.g26tText('wishlist.signin_required', 'Anmeldung erforderlich'));
    var card = document.createElement('div');
    card.className = 'g26-auth__card';
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'g26-auth__close';
    closeBtn.setAttribute('aria-label', window.g26tText('general.accessibility.close', 'Schließen'));
    closeBtn.textContent = '×';
    var title = document.createElement('p');
    title.className = 'g26-auth__title';
    title.textContent = window.g26tText('wishlist.signin_title', 'Ihre Merkliste wartet');
    var copy = document.createElement('p');
    copy.className = 'g26-auth__copy';
    copy.textContent = window.g26tText('wishlist.signin_copy', 'Melden Sie sich an, um Lieblingsstücke zu merken und auf allen Geräten wiederzufinden.');
    var returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    var login = document.createElement('a');
    login.className = 'g26-auth__cta';
    login.href = window.g26url('account/login') + '?return_url=' + returnTo;
    login.textContent = window.g26tText('customer.login.sign_in', 'Anmelden');
    var register = document.createElement('a');
    register.className = 'g26-auth__alt';
    register.href = window.g26url('account/register') + '?return_url=' + returnTo;
    register.textContent = window.g26tText('customer.register.title', 'Konto erstellen');
    var actions = document.createElement('div');
    actions.className = 'g26-auth__actions';
    actions.appendChild(login);
    actions.appendChild(register);
    card.appendChild(closeBtn);
    card.appendChild(title);
    card.appendChild(copy);
    card.appendChild(actions);
    wrap.appendChild(card);
    document.body.appendChild(wrap);
    var onKey = function (e) { if (e.key === 'Escape') dismiss(); };
    var dismiss = function () {
      wrap.remove();
      document.removeEventListener('keydown', onKey);
      if (opener && opener.focus) opener.focus(); // r71: return focus to the heart
    };
    closeBtn.addEventListener('click', dismiss);
    wrap.addEventListener('click', function (e) { if (e.target === wrap) dismiss(); });
    document.addEventListener('keydown', onKey);
    login.focus(); // r71: move keyboard focus into the dialog
  }

  function stashPending(handle) {
    try { localStorage.setItem(PENDING_KEY, JSON.stringify({ handle: handle, ts: Date.now() })); } catch (e) {}
  }

  function resumePending() {
    if (!isLoggedIn()) return;
    var raw = null;
    try { raw = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch (e) {}
    if (!raw || !raw.handle) return;
    try { localStorage.removeItem(PENDING_KEY); } catch (e) {}
    if (Date.now() - (raw.ts || 0) > 3600000) return; // older than an hour — drop
    addItem(raw.handle);
    updateUI();
  }

  function addItem(handle) {
    if (!isLoggedIn()) {
      stashPending(handle);
      promptSignIn();
      return false; // r71: deferred to sign-in — nothing was saved
    }
    var items = getItems();
    if (items.indexOf(handle) === -1) {
      items.push(handle);
      saveItems(items);
      debouncedServerSync();
      var sm = window.GoesslStoreMode && window.GoesslStoreMode.get();
      if (sm && sm.page) {
        var seen = getSeen();
        seen[handle] = { page: sm.page, name: sm.name || '', ts: Date.now() };
        saveSeen(seen);
      }
    }
  }

  function removeItem(handle) {
    var seen = getSeen();
    if (seen[handle]) { delete seen[handle]; saveSeen(seen); }
    var items = getItems().filter(function(h) { return h !== handle; });
    saveItems(items);
    debouncedServerSync();
  }

  function toggleItem(handle) {
    if (hasItem(handle)) { removeItem(handle); return false; }
    // r71: guests get the sign-in prompt, not a filled heart — addItem
    // returns false when it deferred
    return addItem(handle) !== false;
  }

  function getCount() { return getItems().length; }

  // ═══════════════════════════════════════════
  //  OUTFIT GROUPS
  // ═══════════════════════════════════════════

  function getOutfits() {
    try { return JSON.parse(localStorage.getItem(OUTFITS_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveOutfits(outfits) {
    localStorage.setItem(OUTFITS_KEY, JSON.stringify(outfits));
    document.dispatchEvent(new CustomEvent('outfits:updated', { detail: { outfits: outfits } }));
    debouncedServerSync();
  }

  function generateId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'outfit-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  function createOutfit(name, items, gender) {
    var outfits = getOutfits();
    var outfit = {
      id: generateId(),
      name: name || window.g26t('wishlist.outfit_default_name', 'Mein Outfit'),
      created_at: new Date().toISOString(),
      gender: gender || null,
      items: items || []
    };
    outfits.unshift(outfit);
    saveOutfits(outfits);
    return outfit;
  }

  function getOutfit(outfitId) {
    return getOutfits().find(function(o) { return o.id === outfitId; }) || null;
  }

  function updateOutfit(outfitId, updates) {
    var outfits = getOutfits();
    for (var i = 0; i < outfits.length; i++) {
      if (outfits[i].id === outfitId) {
        Object.keys(updates).forEach(function(key) {
          outfits[i][key] = updates[key];
        });
        break;
      }
    }
    saveOutfits(outfits);
  }

  function deleteOutfit(outfitId) {
    var outfits = getOutfits().filter(function(o) { return o.id !== outfitId; });
    saveOutfits(outfits);
  }

  function addToOutfit(outfitId, item) {
    // item: { handle, variant_id, category_slot, position, scale }
    var outfits = getOutfits();
    for (var i = 0; i < outfits.length; i++) {
      if (outfits[i].id === outfitId) {
        // If category_slot is set and not 'accessoires', replace existing in that slot
        if (item.category_slot && item.category_slot !== 'accessoires') {
          outfits[i].items = outfits[i].items.filter(function(existing) {
            return existing.category_slot !== item.category_slot;
          });
        }
        outfits[i].items.push(item);
        break;
      }
    }
    saveOutfits(outfits);
  }

  function removeFromOutfit(outfitId, handle) {
    var outfits = getOutfits();
    for (var i = 0; i < outfits.length; i++) {
      if (outfits[i].id === outfitId) {
        outfits[i].items = outfits[i].items.filter(function(it) {
          return it.handle !== handle;
        });
        break;
      }
    }
    saveOutfits(outfits);
  }

  function addOutfitToCart(outfitId) {
    var outfit = getOutfit(outfitId);
    if (!outfit || !outfit.items.length) return Promise.resolve(null);

    var chosen = outfit.items.filter(function(it) { return it.variant_id; });
    var cartItems = chosen.map(function(it) { return { id: it.variant_id, quantity: 1 }; });

    if (!cartItems.length) return Promise.resolve(null);

    // Vorbestellung (pre-order): a saved outfit holds only handle + variant id,
    // so the selling plan has to be looked up per handle before the add. Without
    // it a pre-order piece books as an ordinary oversold line and the customer
    // is never told the piece is still on its way. Fetches are parallel and
    // fail open — losing the marking is bad, losing the sale is worse.
    // r187: window.g26preorder (layout/theme.liquid) answers per size - a size
    // with stock sells normally and goes in without the plan - and matches the
    // plan by ID (the group name reads "Pre-order" on /en).
    return Promise.all(chosen.map(function(it) {
      if (!it.handle || !window.g26preorder) return null;
      return window.g26preorder.state(it.handle);
    }))
    .then(function(states) {
      states.forEach(function(s, i) {
        var planId = window.g26preorder ? window.g26preorder.planFor(s, cartItems[i].id) : null;
        if (planId) cartItems[i].selling_plan = planId;
      });
      return fetch(window.g26url('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      // r188: never more pieces without the plan than the size has in stock (assets/preorder-cart.js)
      var cap = window.g26preorder && window.g26preorder.capCart ? window.g26preorder.capCart() : Promise.resolve();
      return cap.then(function() {
        document.dispatchEvent(new CustomEvent('cart:open'));
        return data;
      });
    });
  }

  function getOutfitCount() { return getOutfits().length; }

  // ═══════════════════════════════════════════
  //  SERVER SYNC (Xano)
  // ═══════════════════════════════════════════

  function getCustomer() {
    // Namespaced location, with fallback for backwards compat
    return (window.Goessl && window.Goessl.customer) || window.__goesslCustomer || null;
  }

  function isLoggedIn() {
    return getCustomer() !== null;
  }

  function fetchServerWishlist() {
    var customer = getCustomer();
    if (!customer) return Promise.resolve(null);

    return fetch(XANO_BASE + '/storefront/wishlist?shopify_customer_id=' + customer.id, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function(r) { return r.ok ? r.json() : null; })
    .catch(function() { return null; });
  }

  function pushServerWishlist() {
    var customer = getCustomer();
    if (!customer) return;

    fetch(XANO_BASE + '/storefront/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shopify_customer_id: customer.id,
        email: customer.email,
        handles: getItems(),
        outfits: getOutfits()
      })
    }).catch(function() { /* silent fail */ });
  }

  function debouncedServerSync() {
    if (!isLoggedIn()) return;
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(function () {
      // Guard (r39l): never REPLACE the server with a local list that hasn't
      // merged the server's items first — a stale device would silently wipe
      // additions made elsewhere. mergeAndSync pushes itself when they differ.
      if (!serverSynced) { mergeAndSync(); return; }
      pushServerWishlist();
    }, 1000);
  }

  function mergeAndSync() {
    if (!isLoggedIn()) return;

    fetchServerWishlist().then(function(data) {
      if (!data) {
        if (getItems().length > 0 || getOutfits().length > 0) pushServerWishlist();
        return;
      }

      // Merge wishlist handles
      var serverHandles = data.handles || [];
      var local = getItems();
      var merged = local.slice();
      serverHandles.forEach(function(handle) {
        if (merged.indexOf(handle) === -1) merged.push(handle);
      });
      if (merged.length !== local.length) saveItems(merged);

      // Merge outfits by ID
      var serverOutfits = data.outfits || [];
      var localOutfits = getOutfits();
      var mergedOutfits = localOutfits.slice();
      var localIds = {};
      localOutfits.forEach(function(o) { localIds[o.id] = true; });
      serverOutfits.forEach(function(o) {
        if (!localIds[o.id]) mergedOutfits.push(o);
      });
      if (mergedOutfits.length !== localOutfits.length) {
        localStorage.setItem(OUTFITS_KEY, JSON.stringify(mergedOutfits));
        document.dispatchEvent(new CustomEvent('outfits:updated', { detail: { outfits: mergedOutfits } }));
      }

      // Push merged state if anything changed
      if (merged.length !== serverHandles.length || mergedOutfits.length !== serverOutfits.length) {
        pushServerWishlist();
      }

      serverSynced = true;
    });
  }

  // ═══════════════════════════════════════════
  //  UI
  // ═══════════════════════════════════════════

  function updateUI() {
    var count = getCount();

    document.querySelectorAll('[data-wishlist-count]').forEach(function(el) {
      el.textContent = count;
      el.hidden = count === 0;
    });

    document.querySelectorAll('[data-wishlist-toggle]').forEach(function(btn) {
      var handle = btn.dataset.productHandle;
      if (handle) {
        btn.classList.toggle('is-wishlisted', hasItem(handle));
        btn.setAttribute('aria-label', hasItem(handle) ? window.g26t('wishlist.remove', 'Von Merkliste entfernen') : window.g26t('products.product.wishlist_add', 'Zur Merkliste hinzufügen'));
      }
    });
  }

  function bindToggleButtons() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var handle = btn.dataset.productHandle;
      if (!handle) return;

      var added = toggleItem(handle);
      btn.classList.toggle('is-wishlisted', added);
      btn.classList.add('wishlist-pulse');
      setTimeout(function() { btn.classList.remove('wishlist-pulse'); }, 300);
    });
  }

  // ═══════════════════════════════════════════
  //  INIT
  // ═══════════════════════════════════════════

  function init() {
    bindToggleButtons();
    updateUI();
    document.addEventListener('wishlist:updated', updateUI);

    if (isLoggedIn()) mergeAndSync();
  }

  // Expose APIs
  window.Goessl = window.Goessl || {};

  window.Goessl.wishlist = {
    getItems: getItems,
    hasItem: hasItem,
    addItem: addItem,
    removeItem: removeItem,
    toggleItem: toggleItem,
    getCount: getCount,
    getSeen: getSeen,
    updateUI: updateUI,
    isLoggedIn: isLoggedIn,
    promptSignIn: promptSignIn,
    sync: mergeAndSync
  };

  window.Goessl.outfits = {
    getOutfits: getOutfits,
    getOutfit: getOutfit,
    createOutfit: createOutfit,
    updateOutfit: updateOutfit,
    deleteOutfit: deleteOutfit,
    addToOutfit: addToOutfit,
    removeFromOutfit: removeFromOutfit,
    addOutfitToCart: addOutfitToCart,
    getCount: getOutfitCount
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', resumePending);
  } else {
    init();
    resumePending();
  }
})();
